import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  TableCellsIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

export default function FileUploadSource({ type, companyId, onSuccess, onBack }) {
  const [step, setStep] = useState(1); // 1: upload, 2: sheet selection (Excel only), 3: preview, 4: importing
  const [file, setFile] = useState(null);
  const [fileBuffer, setFileBuffer] = useState(null); // Store file for re-upload after sheet selection
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [sheets, setSheets] = useState([]); // Available sheets for Excel files
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(new Set()); // Selected columns for import
  const [columnSearchTerm, setColumnSearchTerm] = useState(''); // Search filter for columns
  
  const onDrop = useCallback(async (acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;
    
    // Validate companyId before upload
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setFile(uploadedFile);
    setFileBuffer(uploadedFile); // Store for potential re-upload
    setName(uploadedFile.name.replace(/\.[^/.]+$/, ''));
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('name', uploadedFile.name);
      formData.append('companyId', companyId);
      
      // Delete Content-Type header so browser sets it with proper multipart boundary
      const response = await api.post('/data-sources/upload', formData, {
        headers: { 'Content-Type': undefined }
      });
      
      // Check if Excel file requires sheet selection
      if (response.data.requiresSheetSelection) {
        setSheets(response.data.sheets);
        // Pre-select the first sheet with data
        const firstWithData = response.data.sheets.find(s => s.hasData) || response.data.sheets[0];
        setSelectedSheet(firstWithData?.name);
        setStep(2); // Go to sheet selection step
      } else {
        setPreview(response.data);
        // Initialize all columns as selected
        const allColumns = response.data.schema?.columns?.map(c => c.name) || [];
        setSelectedColumns(new Set(allColumns));
        setStep(3); // Skip to preview
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to upload file';
      const errorCode = error.response?.data?.code;
      
      if (errorCode === 'COMPANY_REQUIRED') {
        toast.error('Please select a company before uploading files');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  }, [companyId]);
  
  const handleSheetSelection = async () => {
    if (!selectedSheet || !fileBuffer) return;
    
    // Validate companyId before upload
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', fileBuffer);
      formData.append('name', name || fileBuffer.name);
      formData.append('companyId', companyId);
      formData.append('sheetName', selectedSheet);
      
      // Delete Content-Type header so browser sets it with proper multipart boundary
      const response = await api.post('/data-sources/upload', formData, {
        headers: { 'Content-Type': undefined }
      });
      
      setPreview(response.data);
      // Initialize all columns as selected
      const allColumns = response.data.schema?.columns?.map(c => c.name) || [];
      setSelectedColumns(new Set(allColumns));
      setStep(3); // Go to preview
    } catch (error) {
      console.error('Sheet selection upload error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to process selected sheet';
      const errorCode = error.response?.data?.code;
      
      if (errorCode === 'COMPANY_REQUIRED') {
        toast.error('Please select a company before uploading files');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };
  
  const acceptedTypes = type === 'csv' 
    ? { 'text/csv': ['.csv'] }
    : { 
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
      };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024 // 100MB
  });
  
  const handleImport = async () => {
    if (!preview?.dataSource?._id) {
      toast.error('No data source created');
      return;
    }
    
    if (selectedColumns.size === 0) {
      toast.error('Please select at least one column to import');
      return;
    }
    
    setIsImporting(true);
    setStep(4);
    
    try {
      await api.post(`/data-sources/${preview.dataSource._id}/import`, { 
        mode: 'full',
        columns: Array.from(selectedColumns),
        name: name.trim() || preview.dataSource.name // Update name if changed
      });
      toast.success('Import started!');
      onSuccess({ ...preview.dataSource, name: name.trim() || preview.dataSource.name });
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to start import');
      setStep(3);
    } finally {
      setIsImporting(false);
    }
  };
  
  // Column selection handlers
  const handleColumnToggle = (columnName) => {
    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnName)) {
        newSet.delete(columnName);
      } else {
        newSet.add(columnName);
      }
      return newSet;
    });
  };
  
  const handleSelectAllColumns = () => {
    const allColumns = preview?.schema?.columns?.map(c => c.name) || [];
    if (selectedColumns.size === allColumns.length) {
      setSelectedColumns(new Set()); // Deselect all
    } else {
      setSelectedColumns(new Set(allColumns)); // Select all
    }
  };
  
  const Icon = type === 'csv' ? DocumentTextIcon : TableCellsIcon;
  
  // Determine which steps to show based on whether sheet selection is needed
  const hasSheetSelection = type === 'excel' && sheets.length > 1;
  const getSteps = () => {
    if (hasSheetSelection) {
      return [
        { num: 1, label: 'Upload' },
        { num: 2, label: 'Select Sheet' },
        { num: 3, label: 'Preview' },
        { num: 4, label: 'Import' }
      ];
    }
    // Map steps for CSV or single-sheet Excel: 1=Upload, 3=Preview, 4=Import
    return [
      { num: 1, label: 'Upload', displayNum: 1 },
      { num: 3, label: 'Preview', displayNum: 2 },
      { num: 4, label: 'Import', displayNum: 3 }
    ];
  };
  
  const steps = getSteps();
  
  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-4">
        {steps.map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : (s.displayNum || s.num)}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < steps.length - 1 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="card p-8">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                : 'border-surface-300 dark:border-surface-700 hover:border-primary-400'
            }`}
          >
            <input {...getInputProps()} />
            
            {isUploading ? (
              <div>
                <ArrowPathIcon className="w-12 h-12 mx-auto mb-4 text-primary-500 animate-spin" />
                <p className="text-surface-600 dark:text-surface-400">Uploading and analyzing...</p>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-4 text-surface-400" />
                <p className="text-lg font-medium text-surface-900 dark:text-white mb-2">
                  {isDragActive ? 'Drop your file here' : 'Drag & drop a file here'}
                </p>
                <p className="text-surface-500 dark:text-surface-400 mb-4">
                  or click to browse
                </p>
                <p className="text-sm text-surface-400">
                  {type === 'csv' ? 'Supports CSV files' : 'Supports XLS, XLSX files'} • Max 100MB
                </p>
              </>
            )}
          </div>
          
          <div className="flex justify-between mt-6">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Sheet Selection (Excel only) */}
      {step === 2 && sheets.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <DocumentDuplicateIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-surface-900 dark:text-white">
                Select a Sheet to Import
              </h3>
              <p className="text-sm text-surface-500">
                This Excel file contains {sheets.length} sheets. Choose which one to import.
              </p>
            </div>
          </div>
          
          <div className="space-y-2 mb-6">
            {sheets.map((sheet) => (
              <label
                key={sheet.name}
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedSheet === sheet.name
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                    : 'border-surface-200 dark:border-surface-700 hover:border-primary-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="sheet"
                    value={sheet.name}
                    checked={selectedSheet === sheet.name}
                    onChange={(e) => setSelectedSheet(e.target.value)}
                    className="w-4 h-4 text-primary-600 border-surface-300 focus:ring-primary-500"
                  />
                  <div>
                    <span className="font-medium text-surface-900 dark:text-white">
                      {sheet.name}
                    </span>
                    {!sheet.hasData && (
                      <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                        (empty)
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-sm text-surface-500">
                  {sheet.rowCount.toLocaleString()} rows
                </span>
              </label>
            ))}
          </div>
          
          <div className="flex items-center justify-between">
            <button 
              onClick={() => {
                setStep(1);
                setSheets([]);
                setSelectedSheet(null);
              }} 
              className="btn-secondary"
            >
              Back
            </button>
            <button 
              onClick={handleSheetSelection} 
              disabled={!selectedSheet || isUploading}
              className="btn-primary"
            >
              {isUploading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                'Continue with Selected Sheet'
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Preview */}
      {step === 3 && preview && (
        <div className="space-y-6">
          {/* Data Source Name */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder={file?.name?.replace(/\.[^/.]+$/, '') || 'Enter a name for this data source'}
            />
          </div>
          
          {/* File Info Summary */}
          <div className="card p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="font-medium text-surface-900 dark:text-white">{file?.name}</p>
                <p className="text-sm text-surface-500">
                  {preview.schema?.columns?.length} columns • {preview.schema?.totalRows?.toLocaleString()} rows
                  {preview.sheetInfo?.selectedSheet && (
                    <span className="ml-2 text-primary-600 dark:text-primary-400">
                      • Sheet: {preview.sheetInfo.selectedSheet}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          
          {/* Column Selection */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-surface-900 dark:text-white">
                Select Columns ({selectedColumns.size} of {preview.schema?.columns?.length || 0})
              </h3>
              <button
                onClick={handleSelectAllColumns}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {selectedColumns.size === (preview.schema?.columns?.length || 0) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            {/* Column Search - show if more than 8 columns */}
            {(preview.schema?.columns?.length || 0) > 8 && (
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  type="text"
                  value={columnSearchTerm}
                  onChange={(e) => setColumnSearchTerm(e.target.value)}
                  placeholder="Search columns..."
                  className="input pl-9 py-2 text-sm w-full"
                />
                {columnSearchTerm && (
                  <button
                    onClick={() => setColumnSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
              {preview.schema?.columns
                ?.filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                .map((col, i) => (
                  <label
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedColumns.has(col.name)
                        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(col.name)}
                      onChange={() => handleColumnToggle(col.name)}
                      className="rounded text-primary-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate" title={col.name}>
                        {col.name}
                      </p>
                      <p className="text-xs text-surface-500">{col.displayType || col.type}</p>
                    </div>
                  </label>
                ))}
              {preview.schema?.columns?.filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length === 0 && (
                <div className="col-span-full text-center py-4 text-surface-500 text-sm">
                  No columns match "{columnSearchTerm}"
                </div>
              )}
            </div>
          </div>
          
          {/* Data Preview */}
          <div className="card p-4">
            <h3 className="font-medium text-surface-900 dark:text-white mb-3">Data Preview</h3>
            {selectedColumns.size === 0 ? (
              <div className="text-center py-8 text-surface-500 border border-surface-200 dark:border-surface-700 rounded-lg">
                Please select at least one column to preview
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {preview.schema?.columns?.filter(col => selectedColumns.has(col.name)).map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview?.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {preview.schema?.columns?.filter(col => selectedColumns.has(col.name)).map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate">
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <button 
              onClick={() => {
                // Go back to sheet selection if there were multiple sheets, otherwise go to upload
                if (sheets.length > 1) {
                  setStep(2);
                } else {
                  setStep(1);
                }
              }} 
              className="btn-secondary"
            >
              Back
            </button>
            <button onClick={handleImport} disabled={isImporting || selectedColumns.size === 0} className="btn-primary">
              {isImporting && <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />}
              Import {selectedColumns.size > 0 ? `${selectedColumns.size} Columns` : 'Data'}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 4: Importing */}
      {step === 4 && (
        <div className="card p-12 text-center">
          <ArrowPathIcon className="w-12 h-12 mx-auto mb-4 text-primary-500 animate-spin" />
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            Importing Data...
          </h3>
          <p className="text-surface-500 dark:text-surface-400">
            Your data is being imported to BigQuery. This may take a few moments.
          </p>
        </div>
      )}
    </div>
  );
}

